import type {
  AnswerType,
  ChallengeDifficulty,
  ChallengeType,
  Visibility,
} from '@/config/objects/challenges';

/** Where the editor is mounted: the challenge library or an event copy. */
export type ChallengeEditorContext = 'library' | 'event';

/** Creating a new challenge vs editing an existing one. */
export type ChallengeEditorMode = 'create' | 'edit';

/** Activation gate modes for event challenges (DT-157/186/187/188). */
export const ACTIVATION_MODES = [
  'available',
  'manual',
  'gps_proximity',
  'qr_code',
  'ibeacon',
] as const;
export type ActivationMode = (typeof ACTIVATION_MODES)[number];

/** Default GPS activation radius (meters) when none is set. */
export const DEFAULT_ACTIVATION_RADIUS_M = 50;

/**
 * The unified, fully-typed form-value superset for both editor contexts.
 * Numbers are numbers here — string↔number conversion happens only in
 * `mappers.ts` (API boundary) and inside inputs that must render text.
 * Event-only fields are simply ignored by the library mappers/sections.
 */
export interface ChallengeEditorValues {
  // Details
  name: string;
  type: ChallengeType;
  difficulty: ChallengeDifficulty;
  points: number;
  /** Library only. */
  visibility: Visibility;
  answerType: AnswerType;

  // Content
  clue: string;
  hint: string;
  hintCost: number;
  answer: string;
  alternateAnswers: string[];
  choices: string[];
  aiContext: string;
  /** Event only (the library API doesn't accept a description). */
  description: string;

  // Minigame / puzzle
  minigameConfig: Record<string, unknown> | null;
  puzzleGridSize: number;
  isTimed: boolean;

  // Location
  zone: string;
  latitude: number | null;
  longitude: number | null;
  /** Library only: managed greater area (city/region) FK, or null. */
  greaterAreaId: string | null;
  /** Event only: hex map-pin color override (empty = type default). */
  mapIconColor: string;
  /** Event only: custom map-icon image (empty = none). */
  mapIconImageId: string;

  // Media (media ids/URLs as stored by the upload dropzones)
  imageId: string;
  videoId: string;

  // Access code
  accessCode: string;
  accessCodeMessage: string;

  // Gameplay (event only)
  maxCompletions: number | null;
  /** Attempts before the challenge closes. null = inherit the event default, 0 = unlimited. */
  maxAttempts: number | null;
  /** Seconds allowed once the timer starts. null/0 = untimed. */
  timeLimitSeconds: number | null;
  /** Flat points removed per wrong attempt. Mutually exclusive with the percentage. */
  attemptPenaltyPoints: number | null;
  /** Percent of the challenge's points removed per wrong attempt. */
  attemptPenaltyPercent: number | null;
  speedBonuses: number[];
  requiresFacilitator: boolean;
  facilitatorInstructions: string;
  activationMode: ActivationMode;
  activationRadius: number | null;
  ibeaconUuid: string;
  ibeaconMajor: number | null;
  ibeaconMinor: number | null;

  // Read-only server state surfaced in the editor (never sent in payloads)
  /** Generated voiceover URL; updated in place after a regenerate. */
  audioUrl: string;
  /** Server-generated activation QR id (qr_code mode, event only). */
  qrCodeId: string;
}

/** Field-level validation errors keyed by `ChallengeEditorValues` field. */
export type ChallengeEditorErrors = Record<string, string>;

/** Props every section component receives from the orchestrator. */
export interface ChallengeEditorSectionProps {
  values: ChallengeEditorValues;
  errors: ChallengeEditorErrors;
  updateField: <K extends keyof ChallengeEditorValues>(
    key: K,
    value: ChallengeEditorValues[K],
  ) => void;
  context: ChallengeEditorContext;
  mode: ChallengeEditorMode;
}

/**
 * Save handle a sub-resource section (tags, collectibles, artifacts)
 * registers with the editor. The save pipeline runs `save(entityId)` for
 * every dirty handle after the main entity save succeeds.
 */
export interface ChallengeEditorSectionHandle {
  /** Human name used in per-section failure toasts. */
  label: string;
  /** Whether the section has unsaved changes. */
  isDirty: () => boolean;
  /** Persist the section's data (idempotent PUT). */
  save: (entityId: string) => Promise<void>;
}

/** Registration callback passed to controlled sub-resource sections. */
export type RegisterSectionHandle = (
  id: string,
  handle: ChallengeEditorSectionHandle | null,
) => void;

export const DEFAULT_CHALLENGE_EDITOR_VALUES: ChallengeEditorValues = {
  name: '',
  type: 'riddle',
  difficulty: 'medium',
  points: 100,
  visibility: 'private',
  answerType: 'text',
  clue: '',
  hint: '',
  hintCost: 0,
  answer: '',
  alternateAnswers: [],
  choices: [],
  aiContext: '',
  description: '',
  minigameConfig: null,
  puzzleGridSize: 3,
  isTimed: false,
  zone: '',
  latitude: null,
  longitude: null,
  greaterAreaId: null,
  mapIconColor: '',
  mapIconImageId: '',
  imageId: '',
  videoId: '',
  accessCode: '',
  accessCodeMessage: '',
  maxCompletions: null,
  maxAttempts: null,
  timeLimitSeconds: null,
  attemptPenaltyPoints: null,
  attemptPenaltyPercent: null,
  speedBonuses: [],
  requiresFacilitator: false,
  facilitatorInstructions: '',
  activationMode: 'available',
  activationRadius: null,
  ibeaconUuid: '',
  ibeaconMajor: null,
  ibeaconMinor: null,
  audioUrl: '',
  qrCodeId: '',
};
